import { gql } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandXXL, LabelL } from '@sorare/core/src/atoms/typography';
import { RewardImageShine } from '@sorare/core/src/components/rewards/RewardUnlockedAnimation/index.tsx';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav.ts';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  getRewardImage,
  mergeRewardsConfig,
} from '@sorare/core/src/lib/rewards';
import { useCommonGameSet } from '@sorare/core/src/set/index.ts';

import { ButtonsWrapper, Root } from '../styles.tsx';
import type { DualRewardUnlockedStory_rewardConfig } from './__generated__/index.graphql.ts';

export type RewardGroupDisplayProps<
  T extends DualRewardUnlockedStory_rewardConfig =
    DualRewardUnlockedStory_rewardConfig,
> = {
  title: ReactNode;
  subtitle?: ReactNode;
  amount?: string;
  action?: ReactNode;
  rgbColor?: string;
  children?: ReactNode;
  rewardConfigs: T[];
};

const TRANSITION_EASING = 'cubic-bezier(0.75, 0.07, 0.23, 0.89)';

const StyledRoot = styled(Root)`
  --image-size: calc(22 * var(--unit));
`;
const imageEntrance = keyframes`
  from {
    transform:  translateX(50%) scale(1.67) ;
    opacity: 0;
  }
  to {
    transform:  translateX(50%) scale(1) ;
    opacity: 1;
  }
`;

const firstImageAnimation = keyframes`
0% {
  transform:  translateX(50%) scale(1) ;
  opacity: 1;
}
  20% {
    transform:   translateX(50%) scale(0.7);
    opacity: 1;
  }
  100% {
    transform:  translateX(0)scale(0.7) ;
    opacity: 1;
  }
`;

const Images = styled(Horizontal).attrs({ center: true })`
  & > * {
    flex: 1;
  }
`;

const SecondImageWrapper = styled(Vertical).attrs({ center: true })`
  width: calc(var(--image-size) * 0.7);
  height: calc(var(--image-size) * 0.7);
  transition: all 0.5s 0.3s cubic-bezier(0.75, 0.07, 0.23, 0.89);
`;

const EntranceImageWrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  width: var(--image-size);
  height: var(--image-size);
  animation: ${imageEntrance} 0.6s ${TRANSITION_EASING} forwards;
  animation-delay: 0.5s;
  opacity: 0;
  &.animate {
    animation: ${firstImageAnimation} 0.6s ${TRANSITION_EASING} forwards;
  }
`;

const TransitionRewardImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const TransitionTextArea = styled(Vertical).attrs({ gap: 2, center: true })`
  opacity: 0;
  animation: ${fadeIn} 0.65s ${TRANSITION_EASING} forwards;
  animation-delay: 0.55s;
`;

const messages = defineMessages({
  andMore: {
    defaultMessage: 'And...',
  },
});

type DualRewardUnlockedStoryProps = {
  header?: ReactNode;
  onClose: () => void;
  group1Props: RewardGroupDisplayProps;
  group2Props: RewardGroupDisplayProps;
  actionProp?: ReactNode;
  showContinueButton?: boolean;
  hideBackground?: boolean;
  useQuaternaryColorForAction?: boolean;
  useSetBackground?: boolean;
};

export const DualRewardUnlockedStory = ({
  header,
  onClose,
  group1Props,
  group2Props,
  actionProp,
  showContinueButton = true,
  hideBackground,
  useQuaternaryColorForAction = false,
  useSetBackground,
}: DualRewardUnlockedStoryProps) => {
  const isSeriesNav = useSeriesNav();
  const { visual } = useCommonGameSet();
  const [step, setStep] = useState<1 | 2>(1);
  const group1MergedConfigs = mergeRewardsConfig(group1Props.rewardConfigs);
  const group2MergedConfigs = mergeRewardsConfig(group2Props.rewardConfigs);

  const isStep2 = step === 2;
  const currentTextProps = isStep2 ? group2Props : group1Props;

  const shouldUseSetBackground =
    useSetBackground || (isSeriesNav && !!visual.welcomeBackDialog);
  const shouldHideBackground = hideBackground || shouldUseSetBackground;

  const [shine, setShine] = useState(false);
  return (
    <StyledRoot
      style={
        {
          '--rgb-color': currentTextProps.rgbColor || 'var(--c-rgb-green-400)',
          ...(shouldUseSetBackground ? visual.welcomeBackDialog : {}),
        } as unknown as CSSProperties
      }
      className={classNames({ hideBackground: shouldHideBackground })}
    >
      {header}
      <Vertical gap={3} center className="text-center">
        <Images style={{ gap: isStep2 ? 'calc(2 * var(--unit))' : '0' }}>
          <EntranceImageWrapper
            className={classNames({ animate: isStep2 })}
            onAnimationEnd={() => {
              setShine(true);
            }}
          >
            <RewardImageShine
              className={classNames({ visible: shine && !isStep2 })}
              style={
                {
                  '--mask-image': `url(${getRewardImage(group1MergedConfigs[0])})`,
                } as CSSProperties
              }
            />
            {group1MergedConfigs.map(r => (
              <TransitionRewardImage
                key={r.id}
                src={getRewardImage(r)}
                alt=""
              />
            ))}
          </EntranceImageWrapper>
          <SecondImageWrapper
            style={{
              opacity: isStep2 ? 1 : 0,
              transform: isStep2 ? 'translateX(0)' : 'translateX(50%)',
            }}
          >
            {group2MergedConfigs.map(r => (
              <TransitionRewardImage
                key={r.id}
                src={getRewardImage(r)}
                alt=""
              />
            ))}
          </SecondImageWrapper>
        </Images>
        <TransitionTextArea>
          {!!currentTextProps.amount && (
            <BrandXXL color="var(--c-nd-600)">
              {currentTextProps.amount}
            </BrandXXL>
          )}
          <BrandL>{currentTextProps.title}</BrandL>
          {!!currentTextProps.subtitle && (
            <LabelL color="var(--c-nd-600)">{currentTextProps.subtitle}</LabelL>
          )}
        </TransitionTextArea>
      </Vertical>
      <ButtonsWrapper>
        {isStep2 ? (
          <>
            {actionProp || group2Props.action}
            {!!showContinueButton && (
              <Button
                size="large"
                onClick={() => onClose?.()}
                color={
                  (actionProp || group2Props.action) &&
                  !useQuaternaryColorForAction
                    ? 'quaternary'
                    : 'primary'
                }
                disableDebounce
              >
                <FormattedMessage {...glossary.continue} />
              </Button>
            )}
          </>
        ) : (
          <Button size="large" color="secondary" onClick={() => setStep(2)}>
            <FormattedMessage {...messages.andMore} />
          </Button>
        )}
      </ButtonsWrapper>
    </StyledRoot>
  );
};

DualRewardUnlockedStory.fragments = {
  rewardConfig: gql`
    fragment DualRewardUnlockedStory_rewardConfig on AnyRewardConfigInterface {
      id
      ...getRewardImage_anyRewardConfigInterface
      ...mergeRewardsConfig_anyRewardConfigInterface
    }
    ${getRewardImage.fragments.anyRewardConfigInterface}
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<DualRewardUnlockedStory_rewardConfig>,
};
